import React from 'react';
import { Calendar, CalendarDays, DollarSign, Check } from 'lucide-react';
import { Fair, Slot } from '../types';
import { parseCurrencyInput, multiplyCurrency, formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities
import { parseDateOnly } from '../utils/fairStatus'; // Issue #1672

interface ReservationTypeSelectorProps {
  slot: Slot;
  fair: Fair;
  selectedType: 'full_duration' | 'per_day' | null;
  onSelect: (type: 'full_duration' | 'per_day') => void;
  disabled?: boolean;
}

export default function ReservationTypeSelector({
  slot,
  fair,
  selectedType,
  onSelect,
  disabled = false,
}: ReservationTypeSelectorProps) {
  const calculateFullDurationPrice = () => {
    const startDate = parseDateOnly(fair.start_date);
    const endDate = parseDateOnly(fair.end_date);
    if (startDate && endDate) {
      const days = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      return multiplyCurrency(slot.base_price, days).toNumber(); // Issue #905: Decimal.js for precision
    }
    return parseFloat(parseCurrencyInput(slot.base_price)); // Issue #905: Decimal.js for precision
  };

  const calculateDays = () => {
    const startDate = parseDateOnly(fair.start_date);
    const endDate = parseDateOnly(fair.end_date);
    if (startDate && endDate) {
      return Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    }
    return 1;
  };

  const fullDurationPrice = calculateFullDurationPrice();
  const totalDays = calculateDays();
  const allowsPerDay = fair.allows_per_day_reservation ?? false;

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Select Reservation Type</h3>
      <p className="text-gray-600 text-sm">
        Choose how you'd like to reserve this slot for {fair.name}.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Full Duration Option */}
        <button
          onClick={() => onSelect('full_duration')}
          disabled={disabled}
          className={`relative p-6 rounded-xl border-2 text-left transition-all ${
            selectedType === 'full_duration'
              ? 'border-primary bg-teal-50 ring-2 ring-teal-200'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {selectedType === 'full_duration' && (
            <div className="absolute top-4 right-4">
              <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            </div>
          )}

          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-lg ${
              selectedType === 'full_duration' ? 'bg-teal-100' : 'bg-gray-100'
            }`}>
              <CalendarDays className={`w-6 h-6 ${
                selectedType === 'full_duration' ? 'text-secondary' : 'text-gray-600'
              }`} />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 mb-1">Full Duration</h4>
              <p className="text-sm text-gray-600 mb-3">
                Reserve for the entire fair ({totalDays} {totalDays === 1 ? 'day' : 'days'})
              </p>
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-secondary" />
                <span className="text-xl font-bold text-gray-900">
                  {formatCurrency(fullDurationPrice)} {/* Issue #905: Decimal.js formatting */}
                </span>
                <span className="text-sm text-gray-500">total</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {formatCurrency(slot.base_price)}/day x {totalDays} days {/* Issue #905: Decimal.js formatting */}
              </p>
            </div>
          </div>
        </button>

        {/* Per-Day Option */}
        <button
          onClick={() => allowsPerDay && onSelect('per_day')}
          disabled={disabled || !allowsPerDay}
          className={`relative p-6 rounded-xl border-2 text-left transition-all ${
            !allowsPerDay
              ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
              : selectedType === 'per_day'
              ? 'border-primary bg-teal-50 ring-2 ring-teal-200'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
          }`}
        >
          {selectedType === 'per_day' && (
            <div className="absolute top-4 right-4">
              <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            </div>
          )}

          {!allowsPerDay && (
            <div className="absolute top-4 right-4">
              <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                Not available
              </span>
            </div>
          )}

          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-lg ${
              selectedType === 'per_day' ? 'bg-teal-100' : 'bg-gray-100'
            }`}>
              <Calendar className={`w-6 h-6 ${
                selectedType === 'per_day' ? 'text-secondary' : 'text-gray-600'
              }`} />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 mb-1">Per-Day</h4>
              <p className="text-sm text-gray-600 mb-3">
                Select specific dates you want to reserve
              </p>
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-secondary" />
                <span className="text-xl font-bold text-gray-900">
                  {formatCurrency(slot.base_price)} {/* Issue #905: Decimal.js formatting */}
                </span>
                <span className="text-sm text-gray-500">per day</span>
              </div>
              {!allowsPerDay && (
                <p className="text-xs text-amber-600 mt-1">
                  This fair only allows full duration reservations
                </p>
              )}
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}
